<script>
/**
 * @component TreatmentTasks
 * @description Vista de tareas de un protocolo con filtros por tipo y grilla de cards.
 */
import { mapGetters } from "vuex";

import treatmentService from "@/services/treatment";
import treatmentTaskService from "@/services/treatment-task";
import { TASKS_TYPES } from "@/services/constants";

import Spinner from "@/components/ui/Spinner";

/**
 * @description Mapa de iconos Element UI por tipo de tarea
 */
const TYPE_ICONS = {
  all: "el-icon-menu",
  vitals: "el-icon-s-data",
  reports: "el-icon-document",
  appointments: "el-icon-bell",
  checkins: "el-icon-location",
  recommendations: "el-icon-first-aid-kit",
  exercises: "el-icon-bicycle",
  medications: "el-icon-goblet-square",
};

/**
 * @description Colores de badges por tipo de tarea
 */
const TYPE_COLORS = {
  vitals: { bg: "#fce7f3", color: "#be185d" },
  reports: { bg: "#f3e8ff", color: "#7c3aed" },
  appointments: { bg: "#eff6ff", color: "#2563eb" },
  checkins: { bg: "#fef3c7", color: "#92400e" },
  recommendations: { bg: "#f0fdf4", color: "#16a34a" },
  exercises: { bg: "#fff7ed", color: "#ea580c" },
  medications: { bg: "#e6f9f9", color: "#0d7d7d" },
};

export default {
  name: "TreatmentTasks",

  components: {
    Spinner,
  },

  data() {
    return {
      TASKS_TYPES,
      isLoading: false,
      searchFilter: "",
      treatmentId: this.$route.params.id,
      treatmentName: "",
      selectedTaskType: this.$route.params.type || "all",
      tasks: [],
    };
  },

  computed: {
    ...mapGetters(["isPatient"]),

    /**
     * @description True cuando hay una ruta hija activa (create-treatment-task /
     * update-treatment-task). Se usa para ocultar la grilla de cards y dejar
     * únicamente la pantalla del form (que monta en el `<router-view>`).
     * No se puede usar `$route.matched.length` porque el layout Main envuelve
     * todas las rutas, así que matched.length siempre es ≥ 2.
     * @returns {boolean}
     */
    isChildRouteActive() {
      return ["create-treatment-task", "update-treatment-task"].includes(
        this.$route.name
      );
    },

    /** @returns {Array} Tareas filtradas por tipo y búsqueda */
    filteredTasks() {
      return this.tasks.filter((task) => {
        const matchesTaskType =
          this.selectedTaskType === "all"
            ? true
            : task.type === this.selectedTaskType;

        const matchesSearch =
          this.searchFilter === "" ? true : this.includesQuery(task.name);

        return matchesTaskType && matchesSearch;
      });
    },
  },

  created() {
    this.getTreatment();
    this.getAllTasks();

    this.$bus.$on("task-update", (task) => {
      const index = this.tasks.findIndex((t) => t._id === task._id);
      if (index >= 0) {
        this.$set(this.tasks, index, task);
      } else {
        this.tasks.unshift(task);
      }
    });
  },

  methods: {
    includesQuery(string = "") {
      const searchFilter = this.searchFilter.toLocaleLowerCase();
      return string.toLocaleLowerCase().includes(searchFilter);
    },

    filterByType(task) {
      this.selectedTaskType = task;
    },

    getTreatment() {
      treatmentService.getById(this.treatmentId).then((treatment) => {
        this.treatmentName = treatment.name;
      });
    },

    getClassName(type) {
      const className = `${type}-color`;

      if (this.selectedTaskType === type) {
        return `${className} is-active`;
      } else {
        return className;
      }
    },

    getAllTasks() {
      this.isLoading = true;
      return Promise.all(
        Object.keys(TASKS_TYPES).map((type) =>
          treatmentTaskService
            .get({ treatmentID: this.treatmentId, taskType: type })
            .then((tasks) => {
              return tasks.map((task) => ({ ...task, type }));
            })
        )
      )
        .then((tasks) => {
          this.tasks = tasks.flat();
        })
        .finally(() => (this.isLoading = false));
    },

    deleteTask(task) {
      this.$confirm(`¿Desea eliminar el protocolo: ${task.name}?`, "Eliminar", {
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
        type: "warning",
      })
        .then(() => {
          treatmentTaskService.delete({
            taskType: task.type,
            taskID: task._id,
          });

          this.tasks = this.tasks.filter((t) => t._id !== task._id);
        })
        .catch(() => {});
    },

    /**
     * @description Devuelve el ícono Element UI para un tipo de tarea
     * @param {string} type - Tipo de tarea
     * @returns {string}
     */
    getTypeIcon(type) {
      return TYPE_ICONS[type] || "el-icon-document";
    },

    /**
     * @description Devuelve los estilos de color inline para el badge de tipo
     * @param {string} type - Tipo de tarea
     * @returns {Object}
     */
    getTypeBadgeStyle(type) {
      const colors = TYPE_COLORS[type];
      if (!colors) return {};
      return {
        background: colors.bg,
        color: colors.color,
      };
    },

    /**
     * @description Devuelve el nombre friendly del tipo de tarea
     * @param {string} type - Tipo de tarea
     * @returns {string}
     */
    getTypeName(type) {
      return TASKS_TYPES[type]?.name || type;
    },

    /**
     * @description Navega a crear tarea del tipo seleccionado
     * @param {string} taskType - Tipo de tarea
     */
    goToCreateTask(taskType) {
      this.$router.push({
        name: "create-treatment-task",
        params: { taskType },
      });
    },

    /**
     * @description Navega a editar una tarea existente
     * @param {Object} task - Tarea a editar
     */
    goToEditTask(task) {
      this.$router.push({
        name: "update-treatment-task",
        params: { taskID: task._id, taskType: task.type },
      });
    },
  },
};
</script>

<template lang="pug">
div
  //- Cards y filtros se ocultan cuando hay child route activa (crear/editar tarea).
  //- El form de TaskDetail se monta en el `<router-view>` de abajo y queda como
  //- pantalla completa.
  section.tasks-page(v-if="isLoading && !isChildRouteActive")
    spinner

  section.tasks-page(v-else-if="!isChildRouteActive")
    //- Header
    .tasks-header
      .tasks-header__left
        button.btn-back(@click="$router.push({ name: 'treatment' })")
          i.el-icon-arrow-left
        .tasks-header__text
          h1.tasks-title Tareas de {{ treatmentName }}
          span.tasks-subtitle(v-if="filteredTasks.length") {{ filteredTasks.length }} tareas asignadas
          span.tasks-subtitle(v-else) Sin tareas
      .tasks-header__right
        .search-wrapper
          i.el-icon-search.search-icon
          input.search-input(
            v-model="searchFilter"
            placeholder="Buscar tarea..."
          )

    //- Type filter pills
    .type-pills
      button.type-pill(
        :class="{ 'type-pill--active': selectedTaskType === 'all' }"
        @click="filterByType('all')"
      )
        i.el-icon-menu
        span Todos
      button.type-pill(
        v-for="taskType in TASKS_TYPES"
        :key="taskType.type"
        :class="{ 'type-pill--active': selectedTaskType === taskType.type }"
        @click="filterByType(taskType.type)"
      )
        i(:class="getTypeIcon(taskType.type)")
        span {{ taskType.name }}

    //- Task cards grid
    .tasks-grid
      //- Create card
      .task-card.task-card--create(
        v-if="selectedTaskType !== 'all' && !isPatient"
        @click="goToCreateTask(selectedTaskType)"
      )
        i.el-icon-plus.task-card--create__icon
        span.task-card--create__label Nueva tarea

      //- Task cards
      .task-card(
        v-for="task in filteredTasks"
        :key="task._id"
        @click="goToEditTask(task)"
      )
        .task-card__header
          span.task-card__badge(:style="getTypeBadgeStyle(task.type)") {{ getTypeName(task.type) }}
          button.task-card__delete(
            v-if="!isPatient"
            @click.stop="deleteTask(task)"
            title="Eliminar"
          )
            i.el-icon-delete
        h3.task-card__name {{ task.name }}
        p.task-card__desc(v-if="task.description") {{ task.description }}

  router-view
</template>

<style lang="scss" scoped>
/* ── Header ── */
.tasks-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.tasks-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.btn-back {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  i {
    font-size: 16px;
    color: #002d5e;
  }

  &:hover {
    border-color: #002d5e;
  }
}

.tasks-header__text {
  display: flex;
  flex-direction: column;
}

.tasks-title {
  font-size: 20px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
  line-height: 1.2;
}

.tasks-subtitle {
  font-size: 13px;
  color: #94a3b8;
  margin-top: 2px;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.search-input {
  width: 240px;
  padding: 9px 14px 9px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

/* ── Type pills ── */
.type-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.type-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 13px;
  }

  &:hover {
    border-color: #94a3b8;
  }
}

.type-pill--active {
  border-color: #2abfbf;
  background: #e6f9f9;
  color: #0d7d7d;
}

/* ── Tasks grid ── */
.tasks-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

/* ── Create card ── */
.task-card--create {
  border: 1.5px dashed #2abfbf;
  background: #f0fafa;
  border-radius: 12px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e6f9f9;
  }

  &__icon {
    font-size: 24px;
    color: #2abfbf;
  }

  &__label {
    font-size: 13px;
    font-weight: 700;
    color: #2abfbf;
  }
}

/* ── Task card ── */
.task-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  padding: 18px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2abfbf;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.task-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.task-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.task-card__delete {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 14px;
    color: #94a3b8;
  }

  &:hover {
    background: #fef2f2;

    i {
      color: #dc2626;
    }
  }
}

.task-card__name {
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 4px 0;
}

.task-card__desc {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .tasks-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .tasks-grid {
    grid-template-columns: 1fr;
  }

  .tasks-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }
}
</style>
